import { useTranslation } from 'react-i18next';

import * as St from './SinkItems.styles';

import { getUnitType } from '~/UI/pages/OrderSummaryPage';
import { DesignSection, ProductType } from '~/core/enums';
import formatUOM from '~/core/helpers/formatUOM';
import { useGetDesignSectionTranslations } from '~/core/hooks/useGetDesignSectionTranslations';
import { useSink } from '~/core/hooks/useSink';
import { ApplianceModel, ApplianceSource, PricedProduct } from '~/core/models';
import {
  getProductDescription,
  getSinkPrices,
  hasTrendingOrBestSelling,
  imgUrl,
  isBrowsedSink,
} from '~/core/utils';
import { ItemCountAdjustButtonProps } from '~atoms/ItemCountAdjustButton';
import DesignStepContainer from '~molecules/DesignStepContainer';
import SkeletonCard from '~molecules/SkeletonCard';
import ApplianceCard from '~organisms/ApplianceCard';
import RemoveApplianceModal from '~organisms/RemoveApplianceModal';

type SinkItemsProps = {
  sinks: ApplianceModel[];
  skeletonSinks?: ApplianceModel[];
  applianceSource: ApplianceSource;
  onSelectSink: (sink: ApplianceModel) => void;
  onAddSink: (sink: ApplianceModel) => void;
};

const SinkItems = ({
  sinks,
  skeletonSinks = [],
  applianceSource,
  onSelectSink,
  onAddSink,
}: SinkItemsProps) => {
  const { t } = useTranslation();

  const {
    decreaseSinkQuantityHandler,
    getSinkCutoutLaborProductData,
    selectedSinkId,
    selectedSinks,
    setShowRemoveApplianceModal,
    showRemoveApplianceModal,
  } = useSink();

  const {
    modalInfoAdditionalValuesInformation,
    modalInfoAdditionalValuesInformationCustomerSupplied,
  } = useGetDesignSectionTranslations(DesignSection.SINKS, [
    'MODAL.INFO.ADDITIONAL_VALUES_INFORMATION',
    'MODAL.INFO.ADDITIONAL_VALUES_INFORMATION_CUSTOMER_SUPPLIED',
  ]);

  if (sinks.length === 0) {
    return <></>;
  }

  return (
    <St.Container>
      <St.Title>
        {t('APPLIANCE_GROUP.' + applianceSource.toUpperCase())}
      </St.Title>
      <DesignStepContainer>
        {sinks.map((sink) => {
          const unitOfMeasure = formatUOM(
            getUnitType(sink as unknown as PricedProduct)
          );

          const { cutoutLaborProduct } = getSinkCutoutLaborProductData(sink);

          const {
            sinkAndCutoutLaborPriceWithDiscount,
            sinkAndCutoutLaborPrice,
          } = getSinkPrices(sink, cutoutLaborProduct);

          const isSelectedSink = sink.productId === selectedSinkId;

          const dndArgs = { appliance: sink };

          const counterProps: ItemCountAdjustButtonProps = {
            quantity:
              selectedSinks[sink.productId] > 0
                ? selectedSinks[sink.productId]
                : undefined,
            onIncrease: () => onAddSink(sink),
            onDecrease: decreaseSinkQuantityHandler(sink.productId),
          };

          return (
            <ApplianceCard
              applianceType={ProductType.SINK}
              counterProps={counterProps}
              discountPercentage={sink.discountPercentage || undefined}
              dndArgs={dndArgs}
              image={imgUrl(sink.beautyShotImageUrl || sink.imageUrl, 300)}
              isSelected={isSelectedSink}
              key={`sink-${sink.productId}`}
              onMouseDown={() => onSelectSink(sink)}
              priceWithDiscount={sinkAndCutoutLaborPriceWithDiscount}
              priceWithoutDiscount={sinkAndCutoutLaborPrice}
              trendLabel={hasTrendingOrBestSelling(sink.features)}
              title={getProductDescription(sink)}
              tooltipText={t('DESIGN_TOOLBAR.DRAG_ONTO_DESIGN')}
              unitOfMeasure={unitOfMeasure}
              description={
                isBrowsedSink(sink)
                  ? modalInfoAdditionalValuesInformation
                  : modalInfoAdditionalValuesInformationCustomerSupplied
              }
            />
          );
        })}
        {skeletonSinks.map((skeletonSink) => (
          <SkeletonCard
            key={`skeleton-${skeletonSink.productId}`}
            type="appliance"
          />
        ))}
      </DesignStepContainer>

      <RemoveApplianceModal
        isOpen={showRemoveApplianceModal}
        onClose={() => setShowRemoveApplianceModal(false)}
      />
    </St.Container>
  );
};

export default SinkItems;
